import { Button, Container, Flex, Grid } from '@chakra-ui/react';
import { Response } from 'datx-jsonapi';
import NextLink from 'next/link';
import { FC, Fragment, useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import ArrowLeftIcon from '@/assets/icons/ic-arrow-left.svg';

import { useLibraryFilters } from '@/components/ui/features/library/LibraryFilter/LibraryFilter.hooks';
import { LibraryLectureGrid } from '@/components/ui/features/library/LibraryLectureGrid/LibraryLectureGrid';
import { LibrarySearchPanel } from '@/components/ui/features/library/LibrarySearchPanel/LibrarySearchPanel';
import { useAnalytics } from '@/context/Analytics';
import { LectureSort } from '@/enums/LectureSort';
import { StringParam, useQueryParams } from '@/hooks/useQueryParams';
import { useStores } from '@/hooks/useStores';
import { IMetaOmnisearchProps } from '@/interfaces/search';
import { useResourcesInfinite } from '@/lib/datx-jsonapi-react';
import { fetchOmnisearchLectureAccessPermissionsSideload } from '@/queries/lecture-access-permissions';
import { queryOmnisearchLectureInfinite } from '@/queries/omnisearch-thin-lectures';
import { OmnisearchThinLecture } from '@/resources/OmnisearchThinLecture';
import { extractSWRInfiniteData } from '@/utils/swr/extractSWRInfiniteData';

interface ILibraryFiltersPageContentProps {
  pageSize?: number;
}

interface ILibraryFiltersPageContentParams {
  query?: string;
  sort?: string;
  page?: string;
}

export const LibraryFiltersPageContent: FC<ILibraryFiltersPageContentProps> = ({ pageSize = 12 }) => {
  const analytics = useAnalytics();
  const { t } = useTranslation();
  const { store } = useStores();

  const { query, sort = LectureSort.Relevance } = useQueryParams<ILibraryFiltersPageContentParams>({
    query: StringParam,
    sort: StringParam,
  });

  const { filters } = useLibraryFilters();

  const { data, error, size, setSize } = useResourcesInfinite(
    queryOmnisearchLectureInfinite({
      filters: {
        library: 'true',
        scope: 'published',
        with_doi_content: 'true',
        query: query,
        ...filters,
      },
      sort: sort === LectureSort.Relevance && !query ? `-${LectureSort.HeldAt}` : sort,
      pageSize: pageSize,
    }),
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      sideload: fetchOmnisearchLectureAccessPermissionsSideload(store),
    }
  );

  const { meta, ...swrExtract } = useMemo(
    () => extractSWRInfiniteData<OmnisearchThinLecture>(data as Array<Response<OmnisearchThinLecture>>, error, size),
    [data, error, size]
  );

  const isReachingEnd = useMemo(() => {
    if (!data) return false;
    const lastPage = data?.[data.length - 1].meta as IMetaOmnisearchProps;

    return lastPage.currentPage === lastPage.totalPages;
  }, [data]);

  const onLoadMore = useCallback(() => setSize(size + 1), [setSize, size]);

  useEffect(() => {
    if (query && meta?.currentPage === 1) {
      analytics.track('library_search_perform', {
        lectures_num: meta.totalCount,
        query,
      });
    }
  }, [query, meta, analytics]);

  return (
    <Fragment>
      <Container pt={8} px={{ base: 5, md: 10, lg: 5 }}>
        <NextLink href="/library" passHref legacyBehavior>
          <Button as="a" variant="link" colorScheme="black" fontWeight="bold" leftIcon={<ArrowLeftIcon />}>
            {t('library.backToLabel')}
          </Button>
        </NextLink>
      </Container>
      <Container pt="1.875rem" px={{ base: 5, md: 10, lg: 5 }}>
        <Flex flexDir="column" gap={4}>
          <LibrarySearchPanel totalCount={meta?.totalCount} />
          <LibraryLectureGrid
            {...swrExtract}
            isReachingEnd={isReachingEnd}
            onLoadMore={onLoadMore}
            error={(error as unknown) as Error}
            pageSize={pageSize}
          />
        </Flex>
      </Container>
    </Fragment>
  );
};
